import { TffCenterTileFragment } from "@generated-types"
import { useAppData } from "eddev/hooks"
import { useMemo } from "react"
import { formatTffCenterStatus, getAUStateName, TFFCenterStatus } from "./tff-utils"
import dayjs from "dayjs"

type State = {
  priority: number
  state: string
  stateLabel: string
  locations: Location[]
}

type Location = TffCenterTileFragment & { status: TFFCenterStatus }

export function useTFFLocations() {
  const centers = useAppData((data) => data.tffCenters?.nodes)

  return useMemo(() => {
    return centers
      ?.map((center) => {
        const status = formatTffCenterStatus(center.centerInfo!)

        const location: Location = {
          ...center,
          status: status!,
        }

        return location
      })
      .sort((a, b) => {
        const order = b.status.priority - a.status.priority
        if (order === 0) {
          if (a.status.hasDates && b.status.hasDates) {
            const dateA = dayjs(a.centerInfo?.festivalStartDate)
            const dateB = dayjs(b.centerInfo?.festivalStartDate)
            return dateA.diff(dateB)
          } else {
            return a.title!.localeCompare(b.title!)
          }
        }
        return order
      })
  }, [centers])
}

/**
 * Returns TFF locations, grouped by state, with each location ordered by status/date
 */
export function useTFFLocationsByState() {
  const locations = useTFFLocations()

  return useMemo(() => {
    const centersByState: Record<string, State> = {}

    locations?.forEach((location) => {
      if (!location.centerInfo?.state) return

      if (!centersByState[location.centerInfo?.state!]) {
        centersByState[location.centerInfo?.state!] = {
          priority: 0,
          state: location.centerInfo?.state!,
          stateLabel: getAUStateName(location.centerInfo?.state!),
          locations: [],
        }
      }

      const state = centersByState[location.centerInfo?.state!]

      state.locations.push(location)
    })

    const states = Object.values(centersByState)

    states.sort((a, b) => {
      const order = b.locations.length - a.locations.length
      if (order === 0) return a.stateLabel.localeCompare(b.stateLabel)
      return order
    })

    return states
  }, [locations])
}
